import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * "You may also like" upsell carousel in the cart drawer. Fetches real
 * recommendations from Shopify's Product Recommendations API — rather
 * than any hardcoded product list. Each card links straight to that
 * product's page (picking a size there) instead of quick-adding a
 * guessed variant from the cart.
 *
 * @extends {Component}
 */
class CartRecommendationsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelector('.cart-recs__toggle')?.addEventListener('click', this.#handleToggle);
    this.querySelector('.cart-recs__scroller')?.addEventListener('scroll', this.#handleScroll, { passive: true });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#fetchRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    /* Wait for the actual add/update to finish (event.promise) instead
       of re-fetching the instant the event fires. cartLinesUpdate is
       dispatched optimistically, before the real server request
       completes — refetching immediately meant this ran against the
       cart's *old* state, and by the time the real add finished and
       morphed the drawer's section back in (possibly replacing this
       exact element, e.g. on the empty-cart → first-item transition),
       nothing was left to trigger a follow-up fetch, leaving the panel
       permanently blank. Waiting for the promise means this only runs
       once, after the morph has already happened. */
    event.promise
      ?.then(() => {
        if (this.isConnected) this.#fetchRecommendations();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-recommendations] Cart update failed:', error);
      });
  };

  #handleToggle = (event) => {
    const collapsed = this.classList.toggle('is-collapsed');
    event.currentTarget.setAttribute('aria-expanded', String(!collapsed));
  };

  #handleScroll = () => {
    const scroller = this.querySelector('.cart-recs__scroller');
    const fill = this.querySelector('.cart-recs__indicator .cart-free-shipping__fill');
    if (!scroller || !fill) return;

    const max = scroller.scrollWidth - scroller.clientWidth;
    const pct = max > 0 ? (scroller.scrollLeft / max) * 100 : 0;
    fill.style.width = `${pct}%`;
  };

  async #fetchRecommendations() {
    const productId = this.dataset.productId;
    const url = this.dataset.recommendationsUrl;
    if (!productId || !url) return;

    const inCartIds = new Set(
      Array.from(document.querySelectorAll('.cart-items__table-row[data-product-id]')).map(
        (row) => row.getAttribute('data-product-id')
      )
    );

    let products = [];
    try {
      const response = await fetch(`${url}?product_id=${productId}&intent=related&limit=10`);
      if (!response.ok) return;
      const json = await response.json();
      products = (json.products || []).filter((product) => !inCartIds.has(String(product.id))).slice(0, 6);
    } catch (error) {
      console.warn('[cart-recommendations] Fetch failed:', error);
      return;
    }

    const scroller = this.querySelector('.cart-recs__scroller');
    const indicator = this.querySelector('.cart-recs__indicator');
    if (!scroller || !indicator) return;

    if (products.length === 0) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    scroller.hidden = false;
    indicator.hidden = false;

    scroller.innerHTML = products
      .map((product) => {
        const variant = product.variants[0];
        const image = product.featured_image || product.images[0];

        return `
          <a class="cart-rec" href="${product.url}" on:click="#cart-drawer/close">
            <div class="cart-rec__media">
              ${image ? `<img src="${image}" alt="${this.#escape(product.title)}" loading="lazy">` : ''}
              <span class="cart-rec__add" aria-hidden="true">+</span>
            </div>
            <div class="cart-rec__name">${this.#escape(product.title)}</div>
            <div class="cart-rec__price">${this.#formatMoney(variant.price)} AUD</div>
          </a>
        `;
      })
      .join('');

    this.#handleScroll();
  }

  #formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)}`;
  }

  #escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-recommendations-component')) {
  customElements.define('cart-recommendations-component', CartRecommendationsComponent);
}
